/**
 * Dashboard — the two read-only endpoints, and nothing else.
 *
 * Deliberately thin. `GET /admin/stats` and `GET /admin/server-status` are the
 * whole of what the backend offers here, and inventing client-side aggregates
 * on top of them would mean numbers the server cannot vouch for.
 */
import { getServerStatus, getStats } from '../admin-api';
import type { AdminStats, ServerStatus } from '../admin-api.types';
import { badge, errorText, esc, fmtDuration, section } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

function statTile(label: string, value: string | number, attention = false): string {
  return `
    <div class="tg-adm-stat${attention ? ' tg-adm-stat-attn' : ''}">
      <span class="tg-adm-stat-n">${esc(value)}</span>
      <span class="tg-adm-stat-l">${esc(label)}</span>
    </div>`;
}

function view(stats: AdminStats | null, status: ServerStatus | null, error: string): string {
  if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
  if (!stats || !status) return '<p class="tg-adm-empty">Loading…</p>';

  return `
    <div class="tg-adm-stats">
      ${statTile('Players', stats.totalUsers)}
      ${statTile('New today', stats.newToday)}
      ${statTile('New this week', stats.newThisWeek)}
      ${statTile('Withdrawals waiting', stats.pendingWithdrawals, stats.pendingWithdrawals > 0)}
      ${statTile('KYC waiting', stats.pendingKyc, stats.pendingKyc > 0)}
    </div>
    <div class="tg-adm-drawer">
      <div class="tg-adm-kv">
        <div><span>Database</span><span>${status.dbHealthy ? badge('healthy', 'ok') : badge('unreachable', 'bad')}</span></div>
        <div><span>Uptime</span><span>${esc(fmtDuration(status.uptimeSeconds))}</span></div>
        <div><span>Node</span><span class="tg-adm-mono">${esc(status.nodeVersion)}</span></div>
        <div><span>Memory (RSS)</span><span>${esc(status.memoryMb)} MB</span></div>
      </div>
    </div>`;
}

export const dashboardTab: AdminTab = {
  id: 'dashboard',
  label: 'Dashboard',
  group: 'Overview',

  mount(host, ctx) {
    let live = true;
    let stats: AdminStats | null = null;
    let status: ServerStatus | null = null;
    let error = '';

    const paint = () => {
      host.innerHTML = section(
        'Dashboard',
        'Population and process health, read straight from the server.',
        view(stats, status, error),
        '<button type="button" class="tg-adm-btn" data-act="refresh">Refresh</button>',
      );
    };

    const load = async () => {
      error = '';
      try {
        // Settled together rather than awaited in sequence: a slow stats query
        // should not hold back the process readout, and one failing endpoint
        // should still let the other render.
        const [s, st] = await Promise.all([getStats(), getServerStatus()]);
        if (!live) return;
        stats = s;
        status = st;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    };

    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-act="refresh"]')) {
        ctx.notify('Refreshed.');
        void load();
      }
    };
    host.addEventListener('click', onClick);

    paint();
    void load();

    return () => { live = false; host.removeEventListener('click', onClick); };
  },
};
